/**
 * ClinicalPreferencesCard
 * ────────────────────────────────────────────────────
 * M5 — Lumina clinical prefs:
 *   A. Specialty (links to onboarding if unset)
 *   B. Clinical Signals toggles (which classes to surface)
 *   C. Note Style:
 *      - Structure preference (SOAP / HPI-first / narrative / bullet)
 *      - Carry-forward toggles (meds, PMH, FSH, ROS, exam)
 *      - Template library (prebuilt templates)
 *      - Learning indicator (acceptance-rate heuristic)
 *
 * All prefs persist in localStorage — mock, surface-level.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Stethoscope, Sparkles, FileText, Brain, RefreshCw, CheckCircle2,
  AlertCircle, ShieldCheck, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSpecialty } from '../../contexts/SpecialtyContext';
import { useAppMode } from '../../contexts/AppModeContext';
import NoteAndVisitConfigurator from './NoteAndVisitConfigurator';
import VisitTypeSettings from '../echo/scheduler/v2/VisitTypeSettings';

const SIGNAL_CLASSES = [
  { id: 'care_gaps',  label: 'Care gaps',           icon: ShieldCheck, tone: 'green',  desc: 'Surfaces overdue screens, labs, and vaccines per specialty.' },
  { id: 'coding',     label: 'Coding opportunities',icon: Sparkles,    tone: 'purple', desc: 'Level-of-service hints + missing HCC / chronic-condition capture.' },
  { id: 'compliance', label: 'Risk & compliance',   icon: AlertCircle, tone: 'amber',  desc: 'SI mentions, controlled substance / PDMP reminders, PHQ-9 cadence.' },
  { id: 'context',    label: 'Context nudges',      icon: Info,        tone: 'blue',   desc: 'Notes the patient once mentioned, allergy double-checks, vocab prompts.' },
];

const STRUCTURE_OPTIONS = [];

const CARRY_FWD_FIELDS = [];

const TEMPLATES = [];

const LS = {
  signals: 'lumina_cp_signals',
  structure: 'lumina_cp_structure',
  carry: 'lumina_cp_carry',
};

const readJSON = (k, def) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : def; } catch (_) { return def; }
};
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) {} };

const Switch = ({ on, onChange, testId }) => (
  <button
    type="button"
    onClick={onChange}
    className={`h-5 w-9 rounded-full relative transition ${on ? 'bg-slate-900' : 'bg-slate-300'}`}
    data-testid={testId}
  >
    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${on ? 'left-4' : 'left-0.5'}`} />
  </button>
);

export const ClinicalPreferencesCard = () => {
  const { specialty, specialties, setSpecialtyId } = useSpecialty();
  const { isDemo } = useAppMode();

  const [signals, setSignals] = useState(() => readJSON(LS.signals, { care_gaps: true, coding: true, compliance: true, context: true }));

  useEffect(() => writeJSON(LS.signals, signals), [signals]);

  /* Mock learning stats — would normally be computed from note-edit diffs. */
  const learningStats = { accepted: 142, edited: 38, rejected: 7 };
  const learningPct = Math.round((learningStats.accepted / (learningStats.accepted + learningStats.edited + learningStats.rejected)) * 100);

  return (
    <Card className="border-slate-200" data-testid="clinical-prefs-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold inline-flex items-center gap-2">
          <Stethoscope className="h-4 w-4 text-slate-700" /> Clinical Preferences
          <Badge className="ml-1 text-[9px] bg-indigo-50 text-indigo-700 border border-indigo-200">Lumina · Phase 1</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* A. Specialty */}
        <section className="space-y-2" data-testid="cp-specialty">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-semibold text-slate-800">My specialty</p>
            <Badge className="text-[9px] bg-slate-100 text-slate-700 border-0">{specialty.short}</Badge>
            <span className="text-[11px] text-slate-500">— {specialty.label}</span>
          </div>
          <select
            value={specialty.id}
            onChange={(e) => { setSpecialtyId(e.target.value); toast.success('Specialty updated'); }}
            className="w-full max-w-md text-[12px] px-3 py-2 rounded-lg border border-slate-200 bg-white focus:border-slate-400 outline-none"
            data-testid="cp-specialty-select"
          >
            {specialties.map((s) => <option key={s.id} value={s.id}>{s.label} — {s.tagline}</option>)}
          </select>
          <p className="text-[11px] text-slate-500">Changes vocabulary, CPT hints, clinical signals, and (for some specialties) the note layout.</p>
        </section>

        {/* B. Clinical Signals toggles */}
        <section className="space-y-2" data-testid="cp-signals">
          <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
            <Sparkles className="h-3.5 w-3.5 text-slate-600" />
            <p className="text-[13px] font-semibold text-slate-800">Clinical Signals</p>
            <p className="text-[11px] text-slate-400">Shown at the top of the AI Suggestions panel</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SIGNAL_CLASSES.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.id} className="flex items-start gap-2 p-2 rounded-lg border border-slate-100" data-testid={`cp-signal-${c.id}`}>
                  <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${c.tone === 'amber' ? 'text-amber-600' : c.tone === 'green' ? 'text-emerald-600' : c.tone === 'purple' ? 'text-violet-600' : 'text-blue-600'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-slate-800">{c.label}</p>
                    <p className="text-[10px] text-slate-500 leading-snug">{c.desc}</p>
                  </div>
                  <Switch on={signals[c.id]} onChange={() => setSignals({ ...signals, [c.id]: !signals[c.id] })} testId={`cp-signal-toggle-${c.id}`} />
                </div>
              );
            })}
          </div>
        </section>

        {/* C. Note Format, Visit Type Routing, Templates (consolidated) */}
        <section className="space-y-3" data-testid="cp-note-style">
          <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
            <FileText className="h-3.5 w-3.5 text-slate-600" />
            <p className="text-[13px] font-semibold text-slate-800">Note Format &amp; Visit Type Configuration</p>
            <p className="text-[11px] text-slate-400 truncate">One pane for note structure, per-visit-type routing, and template management</p>
          </div>

          {isDemo ? (
            <NoteAndVisitConfigurator />
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 flex items-start gap-2.5" data-testid="note-cfg-real-gate">
              <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                <FileText className="h-3.5 w-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[12px] font-semibold text-slate-700">Available in Demo mode</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Note format / per-visit-type routing / templates will move to a server-backed
                  preferences API in a future release. Until then, this configurator is reachable
                  in Demo mode only.
                </p>
              </div>
            </div>
          )}

          {/* Visit Type Parameters — full editor mirrored from Schedule → Settings → Visit Types.
              Same fields: basic info, staff labels, visit behavior flags, visit frequency,
              clinical sections, document display settings. Edits flow through the same store
              (DEMO_VISIT_TYPES_CATALOG / scheduler API) as the Schedule tab. */}
          <div className="pt-2" data-testid="cp-visit-type-parameters">
            <div className="flex items-center gap-2 pb-1 border-b border-slate-100 mb-3">
              <Stethoscope className="h-3.5 w-3.5 text-slate-600" />
              <p className="text-[13px] font-semibold text-slate-800">Visit Type Parameters</p>
              <p className="text-[11px] text-slate-400 truncate">Full per-visit-type configuration · synced with Schedule → Settings</p>
            </div>
            <VisitTypeSettings />
          </div>

          {/* Learning engine */}
          <div className="p-3 rounded-lg border border-indigo-100 bg-indigo-50/40" data-testid="cp-learning">
            <div className="flex items-center gap-2">
              <Brain className="h-3.5 w-3.5 text-indigo-700" />
              <p className="text-[11px] font-bold text-slate-800">Learning engine</p>
              <Badge className="ml-auto text-[9px] bg-white text-indigo-700 border border-indigo-200">{learningPct}% accepted as-is</Badge>
            </div>
            <p className="text-[10px] text-slate-600 mt-1">
              Lumina is watching how you edit generated notes and tuning its structure + phrasing to match your preferences.
            </p>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[10px]">
              <div className="text-center">
                <p className="font-bold text-emerald-700">{learningStats.accepted}</p>
                <p className="text-slate-500">Accepted</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-amber-700">{learningStats.edited}</p>
                <p className="text-slate-500">Edited</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-red-700">{learningStats.rejected}</p>
                <p className="text-slate-500">Rejected</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="mt-2 h-6 text-[10px] text-slate-600" onClick={() => toast.info('Learning model reset for your account')}>
              <RefreshCw className="h-2.5 w-2.5 mr-1" /> Reset learning
            </Button>
          </div>
        </section>
      </CardContent>
    </Card>
  );
};

export default ClinicalPreferencesCard;
